import React, { useState, useEffect } from 'react';
import { agentService } from '../API/agentService';

const ConfigEditor = ({ onConfigUpdated }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      // For now, show current agent configuration
      const projects = await agentService.getProjects();
      const agents = await agentService.getAgentStatus();

      setConfig({
        projects,
        agents,
        settings: {
          autoStandup: true,
          emailNotifications: false,
          slackNotifications: false,
          reportRetentionDays: 30
        }
      });
    } catch (error) {
      console.error('Failed to load config:', error);
      setMessage('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Mock save - in real implementation would call agentService.updateConfig
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMessage('Configuration saved successfully!');
      onConfigUpdated();

      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleAgentEnabled = (agentId) => {
    setConfig(prev => ({
      ...prev,
      agents: prev.agents.map(agent =>
        agent.id === agentId
          ? { ...agent, enabled: !agent.enabled, status: !agent.enabled ? 'active' : 'disabled' }
          : agent
      )
    }));
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="config-editor loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="config-editor">
      {/* Header */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Agent Configuration</h3>
          <p className="card-subtitle">
            Manage agent settings, enable/disable agents, and configure system preferences
          </p>
        </div>

        {message && (
          <div className={`message ${message.includes('Failed') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <div className="config-actions">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '💾 Saving...' : '💾 Save Configuration'}
          </button>
        </div>
      </div>

      <div className="grid grid-2">
        {/* Agent Settings */}
        <div className="dashboard-card">
          <div className="card-header">
            <h4 className="card-title">Agent Settings</h4>
            <p className="card-subtitle">Enable/disable individual agents</p>
          </div>

          <div className="agents-config">
            {config.agents.map(agent => (
              <div key={agent.id} className="agent-config-item">
                <div className="agent-config-info">
                  <h5>{agent.name}</h5>
                  <p>{agent.description}</p>
                </div>

                <div className="agent-config-controls">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={agent.enabled}
                      onChange={() => toggleAgentEnabled(agent.id)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`status-indicator ${agent.status}`}>
                    {agent.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Settings */}
        <div className="dashboard-card">
          <div className="card-header">
            <h4 className="card-title">System Settings</h4>
            <p className="card-subtitle">General system preferences</p>
          </div>

          <div className="settings-form">
            <div className="setting-item">
              <div className="setting-info">
                <label>Automatic Daily Standup</label>
                <p>Run daily standup automatically at 9 AM</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.settings.autoStandup}
                  onChange={(e) => updateSetting('autoStandup', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label>Email Notifications</label>
                <p>Send email notifications for critical issues</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.settings.emailNotifications}
                  onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label>Slack Notifications</label>
                <p>Send notifications to Slack channels</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.settings.slackNotifications}
                  onChange={(e) => updateSetting('slackNotifications', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label>Report Retention (days)</label>
                <p>How long to keep agent reports</p>
              </div>
              <input
                type="number"
                min="1"
                max="365"
                value={config.settings.reportRetentionDays}
                onChange={(e) => updateSetting('reportRetentionDays', parseInt(e.target.value))}
                className="setting-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-card">
        <div className="card-header">
          <h4 className="card-title">Quick Actions</h4>
        </div>

        <div className="quick-actions-grid">
          <button className="action-button">
            🔄 Reset All Agents
          </button>
          <button className="action-button">
            📊 Export Configuration
          </button>
          <button className="action-button">
            📁 Backup Reports
          </button>
          <button className="action-button">
            🧹 Clear Old Reports
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigEditor;